import { useQuery } from '@tanstack/react-query'
import { api } from './api'

/**
 * A colleague's directory photo as an object URL, or `null` when there is none to show.
 *
 * One query per address with no expiry, so a grid of cards (or a members list reopened) naming the
 * same person fetches that photo once per session, however many avatars ask. The URL is never
 * revoked for the same reason: every avatar for that person shares it. `null` (no photo, no
 * permission, a failed fetch) is a normal answer — the avatar shows initials — so this never
 * retries and never reports.
 */
export function usePersonPhoto(email: string): string | null {
  const address = email.trim().toLowerCase()
  const { data } = useQuery({
    queryKey: ['directory-photo', address],
    queryFn: async () => {
      const blob = await api.directoryPhoto(address)
      return blob && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null
    },
    enabled: address.includes('@'),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })
  return data ?? null
}
